<template>
  <UiComboboxVee
    name="userMainRole"
    :label="$t('term.role')"
    :options="roleOptions"
    :isDisabled="!canEditRole"
    :isRequired="true"
    v-model="proxyMainRole" />
</template>

<script lang="ts">
import type { LabManagementUser } from '@/globals/typescript/models/LabManagementUser'
import { getRoleDisplayName } from '@/globals/typescript/utils/roleUtils'
import { useUserStore } from '@/stores/userStore'
import {
  labManagementRoles,
  type TLabManagementRole,
} from '@team-milva/lab-models/user'
import { mapState } from 'pinia'
import type { PropType } from 'vue'

type TRoleSelectionOption = {
  value: TLabManagementRole
  text: string
}

export default {
  name: 'RoleSelection',
  emits: ['update:userMainRole'],
  props: {
    user: {
      type: Object as PropType<LabManagementUser | null>,
      default: null,
    },
    userMainRole: {
      type: String as PropType<TLabManagementRole>,
      required: true,
    },
  },
  computed: {
    ...mapState(useUserStore, ['currentUser']),
    isOwnUser(): boolean {
      return !!this.user && this.user.id === this.currentUser?.id
    },
    allowedRoles(): TLabManagementRole[] {
      return this.currentUser?.getAllowedRolesToManage() ?? []
    },
    canEditRole(): boolean {
      if (this.isOwnUser) {
        return this.currentUser?.canEditOwnRole() ?? false
      }

      return true
    },
    proxyMainRole: {
      get(): TLabManagementRole {
        return this.userMainRole
      },
      set(value: TLabManagementRole) {
        this.$emit('update:userMainRole', value)
      },
    },
    // ComboboxVee has no per-option disabled state, so a role that isn't
    // assignable by the current viewer is left out of the list entirely
    // rather than shown disabled — except the current value, which is
    // always included so the trigger never shows blank (e.g. a User's own
    // role is never in their own getAllowedRolesToManage(), since a User
    // manages nobody).
    selectableRoles(): TLabManagementRole[] {
      return this.allowedRoles.includes(this.userMainRole)
        ? this.allowedRoles
        : [...this.allowedRoles, this.userMainRole]
    },
    roleOptions(): TRoleSelectionOption[] {
      return labManagementRoles
        .filter((role) => this.selectableRoles.includes(role))
        .map((role) => ({
          value: role,
          text: getRoleDisplayName(role),
        }))
    },
  },
}
</script>
